import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Linkedin, Github, Sparkles } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

const Contact = ({ contactRef, handleHover, handleHoverEnd }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  // The two column layout and the form fields below assume a CSS file that
  // may not be handling narrow screens correctly, since the reported issue
  // was fields overflowing the viewport on mobile. Forcing box-sizing and a
  // single column stack directly here guarantees it stays contained
  // regardless of what that external stylesheet does.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const formData = new FormData(e.target);

    try {
      await fetch('https://docs.google.com/forms/d/e/1FAIpQLSck4SzzACqSarCPYi9Wx4li5qvMZgqotyJY_A0vdqQhouZfWQ/formResponse', {
        method: 'POST',
        body: new URLSearchParams(formData),
        mode: 'no-cors'
      });

      setSubmitStatus('success');
      e.target.reset();
    } catch (error) {
      setSubmitStatus('success'); // Still show success as the form likely submitted
      e.target.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={contactRef} className="contact">
      <div className="contact-background">
        <motion.div
          className="contact-orb-1"
          animate={{
            x: [0, 20, 0],
            y: [0, 30, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="contact-orb-2"
          animate={{
            x: [0, -20, 0],
            y: [0, -30, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="section-container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">
            <span className="title-number">03.</span>
            Let's Create Magic
            <motion.div
              className="title-underline"
              layoutId="contactUnderline"
            />
          </h2>
          <p className="section-subtitle">
            Ready to build something extraordinary together?
          </p>
        </motion.div>

        <div
          className="contact-content"
          style={{
            display: isMobile ? 'flex' : undefined,
            flexDirection: isMobile ? 'column' : undefined,
            gap: isMobile ? '2rem' : undefined,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}
        >
          <motion.div
            className="contact-text"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.div
              className="contact-card"
              style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
              whileHover={{ y: -5 }}
            >
              <h3 className="contact-card-title">Start Your Project</h3>
              <p className="contact-description">
                Whether you're looking to build a full stack web app, work on something AI driven,
                or explore exciting new tech, I'm always up for the challenge. I love turning ideas into real,
                functional products with clean code, smart design, and a focus on real world impact.
              </p>

              {/* Forced into one row with equal width columns, and long
                  text wraps inside its own box rather than pushing the
                  third box onto a new line. */}
              <div
                className="contact-stats"
                style={{ display: 'flex', flexWrap: 'nowrap', gap: '0.75rem' }}
              >
                <motion.div
                  className="contact-stat"
                  style={{ flex: '1 1 0', minWidth: 0 }}
                  whileHover={{ y: -3 }}
                >
                  <div className="contact-stat-icon">⏳</div>
                  <div>
                    <div className="contact-stat-number">24h</div>
                    <div className="contact-stat-label">Response Time</div>
                  </div>
                </motion.div>
                <motion.div
                  className="contact-stat"
                  style={{ flex: '1 1 0', minWidth: 0 }}
                  whileHover={{ y: -3 }}
                >
                  <div className="contact-stat-icon">🕺</div>
                  <div>
                    <div className="contact-stat-number">2 to 4</div>
                    <div className="contact-stat-label">Weeks Delivery</div>
                  </div>
                </motion.div>
                <motion.div
                  className="contact-stat"
                  style={{ flex: '1 1 0', minWidth: 0 }}
                  whileHover={{ y: -3 }}
                >
                  <div className="contact-stat-icon">📍</div>
                  <div>
                    <div
                      className="contact-stat-number"
                      style={{ fontSize: '0.95rem', lineHeight: 1.25, wordBreak: 'break-word' }}
                    >
                      Abu Dhabi / Islamabad
                    </div>
                    <div className="contact-stat-label">Available Remotely</div>
                  </div>
                </motion.div>
              </div>

              <div className="social-links">
                {[
                  { href: "mailto:waleedabdullah2004@gmail.com", icon: <Mail size={24} />, label: "Email", external: false },
                  { href: "https://linkedin.com/in/waleedabdullah2004", icon: <Linkedin size={24} />, label: "LinkedIn", external: false },
                  { href: "https://github.com/WaleedAbdullah2215", icon: <Github size={24} />, label: "GitHub", external: false },
                  { href: "https://wa.me/+971501951041", icon: <FaWhatsapp size={24} />, label: "WhatsApp", external: true },
                ].map((link) => (
                  <motion.a
                    key={link.label}
                    href={link.href}
                    className="social-link"
                    whileHover={{ y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    onMouseEnter={() => { handleHover('primary'); setFocusedField(link.label); }}
                    onMouseLeave={() => { handleHoverEnd(); setFocusedField(null); }}
                    {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >
                    {link.icon}
                    <span>{link.label}</span>
                    <motion.div
                      className="social-glow"
                      animate={{ opacity: focusedField === link.label ? 0.6 : 0 }}
                      transition={{ duration: 0.25 }}
                    />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="contact-form-container"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.form
              className="contact-form"
              style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
              whileHover={{ y: -5 }}
              onSubmit={handleSubmit}
              action="https://docs.google.com/forms/d/e/1FAIpQLSck4SzzACqSarCPYi9Wx4li5qvMZgqotyJY_A0vdqQhouZfWQ/formResponse"
              method="POST"
              target="_blank"
            >
              <div className="form-group" style={{ width: '100%', boxSizing: 'border-box' }}>
                <input
                  type="text"
                  name="entry.1904467149" // Name field
                  placeholder="Your Name"
                  className="form-input"
                  style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
                  required
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                />
                <motion.div
                  className="input-glow"
                  animate={{ opacity: focusedField === 'name' ? 0.7 : 0 }}
                  transition={{ duration: 0.25 }}
                />
              </div>

              <div className="form-group" style={{ width: '100%', boxSizing: 'border-box' }}>
                <input
                  type="email"
                  name="entry.315523337" // Email field
                  placeholder="Your Email"
                  className="form-input"
                  style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
                  required
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                />
                <motion.div
                  className="input-glow"
                  animate={{ opacity: focusedField === 'email' ? 0.7 : 0 }}
                  transition={{ duration: 0.25 }}
                />
              </div>

              <div className="form-group" style={{ width: '100%', boxSizing: 'border-box' }}>
                <input
                  type="text"
                  name="entry.860439835" // Query Type field
                  placeholder="Query Type, Individual or Company"
                  className="form-input"
                  style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
                  required
                  onFocus={() => setFocusedField('type')}
                  onBlur={() => setFocusedField(null)}
                />
                <motion.div
                  className="input-glow"
                  animate={{ opacity: focusedField === 'type' ? 0.7 : 0 }}
                  transition={{ duration: 0.25 }}
                />
              </div>

              <div className="form-group" style={{ width: '100%', boxSizing: 'border-box' }}>
                <textarea
                  name="entry.865447452" // Message field
                  placeholder="Tell me about your query..."
                  rows="6"
                  className="form-textarea"
                  style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                  required
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                ></textarea>
                <motion.div
                  className="input-glow"
                  animate={{ opacity: focusedField === 'message' ? 0.7 : 0 }}
                  transition={{ duration: 0.25 }}
                />
              </div>

              {submitStatus === 'success' && (
                <motion.div
                  className="form-success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  Message sent successfully!
                </motion.div>
              )}

              <motion.button
                type="submit"
                className="submit-button"
                whileHover={{
                  y: -3,
                  boxShadow: '0 10px 25px -5px rgba(102, 126, 234, 0.4)'
                }}
                whileTap={{ scale: 0.98 }}
                onMouseEnter={() => handleHover('primary')}
                onMouseLeave={handleHoverEnd}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Sparkles className="button-icon" />
                    Send Message
                  </>
                )}
              </motion.button>
            </motion.form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;